import type { GoalProps } from './types'

export type { GoalProps, PiletAnalyticsApi } from './types'

// The third-party analytics script. It is NOT in index.html (that would beacon on
// every load, before any decision); it is injected here only once the user has
// granted the `analytics` category — which is what makes the recorded consent
// actually *enforced* rather than merely stored.
const PLAUSIBLE_SRC = 'https://plausible.io/js/pa-pGJKru4bbBzfD7JG6TEDw.js'

let enabled = false

/**
 * Load Plausible and open the goal gate. Called from the consent loaders when
 * `analytics` is granted; idempotent, because consent can be re-broadcast.
 *
 * Consent is never revoked here, for the usual tag-loader reason: a third-party
 * script already on the page cannot be truly unloaded, so a withdrawal takes effect
 * on the next full load, when it is simply not injected again.
 */
export function enablePlausible (): void {
  if (enabled) return
  enabled = true
  const script = document.createElement('script')
  script.async = true
  script.src = PLAUSIBLE_SRC
  document.head.appendChild(script)
}

/** Whether goals may be recorded — the gate every goal goes through. */
export function isAnalyticsEnabled (): boolean {
  return enabled
}

// Plausible transports props as strings. Coerce here rather than at each call site,
// and drop empty ones outright — an absent prop is a cleaner "not applicable" in the
// dashboard than the string "undefined", which would otherwise become a value of its
// own and eat into the site's prop cardinality.
function toStringProps (props: GoalProps): Record<string, string> {
  const out: Record<string, string> = {}
  Object.entries(props).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    out[key] = String(value)
  })
  return out
}

/**
 * Record a goal. Dropped unless the `analytics` category was granted.
 *
 * The gate matters more than it looks: `window.plausible` always exists (the inline
 * stub in index.html), and it buffers into `plausible.q` forever. Calling through
 * without this check would grow that queue for the whole session on a refusal, and
 * replay an hour of goals the moment consent was granted — reporting behaviour
 * recorded while the visitor had said no.
 */
export function trackGoal (name: string, props?: GoalProps): void {
  if (!enabled) return
  try {
    window.plausible?.(name, props ? { props: toStringProps(props) } : undefined)
  } catch (err) {
    console.warn('[analytics] plausible goal failed', err)
  }
}
